/**
 * Bridges the unlocked wallet (mnemonic) to the shielded keys (UFVK,
 * Unified Address) and registers the UA with teleswap-bot for
 * @username resolution.
 *
 * Cycle:
 *   wallet unlocks  →  derive UFVK + UA in a useEffect  →
 *     store in context  →  POST to /api/telecrypt/register  →
 *     (background) start balance polling
 *
 * The derivation is lazy (only fires when the wallet is unlocked) and
 * its result is memoised — opening the Send screen for the tenth time
 * doesn't re-derive.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useUnlockedWallet } from "../wallet";
import { deriveShieldedKeysFromMnemonic, type ShieldedKeys } from "./ufvk";
import { registerMyTelecryptUA } from "../lib/teleswap-client";

export type ShieldedKeysState =
  | { kind: "idle" }
  | { kind: "deriving" }
  | { kind: "ready"; keys: ShieldedKeys }
  | { kind: "error"; message: string };

interface ShieldedKeysApi {
  state: ShieldedKeysState;
  /** Quick accessor for consumers that only care when keys are ready. */
  keys: ShieldedKeys | null;
}

const Ctx = createContext<ShieldedKeysApi | null>(null);

export function ShieldedKeysProvider({ children }: { children: ReactNode }) {
  const wallet = useUnlockedWallet();
  const [state, setState] = useState<ShieldedKeysState>({ kind: "idle" });

  useEffect(() => {
    if (!wallet) {
      setState({ kind: "idle" });
      return;
    }
    let cancelled = false;
    setState({ kind: "deriving" });
    (async () => {
      try {
        const keys = await deriveShieldedKeysFromMnemonic(wallet.phrase, 0);
        if (cancelled) return;
        setState({ kind: "ready", keys });
        // Fire-and-forget: register the UA so other ZcashWallet users
        // can resolve us by @username. Failures are non-fatal — the
        // wallet still works locally without the directory entry.
        registerMyTelecryptUA({
          unified_address: keys.unifiedAddress,
        }).catch((e: unknown) => {
          // eslint-disable-next-line no-console
          console.warn("[telecrypt] UA registration failed (non-fatal):", e);
        });
      } catch (e) {
        if (cancelled) return;
        setState({
          kind: "error",
          message: e instanceof Error ? e.message : String(e),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [wallet]);

  const api = useMemo<ShieldedKeysApi>(
    () => ({
      state,
      keys: state.kind === "ready" ? state.keys : null,
    }),
    [state],
  );

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useShieldedKeys(): ShieldedKeysApi {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error(
      "useShieldedKeys must be used inside <ShieldedKeysProvider>",
    );
  }
  return v;
}
